import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Users, Clock, CheckCircle, AlertCircle, XCircle, BarChart3, ClipboardList, Folder, Ship, Shield, Zap, Wrench, ChevronDown, Filter, Target, Anchor,ChevronUp, Maximize2, Minimize2, CheckSquare } from 'lucide-react';
import EmployeeShiftCalender from '../projectmappingdashboard/EmployeeShiftCalender';
import WorkOrderCalender from '../projectmappingdashboard/WorkOrderCalender';
import DepartmentWiseMapping from '../projectmappingdashboard/DepartmentWiseMapping';
import { getRequest, postRequest } from '../../services/apiService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import UpdateTaskINSDAS from './UpdateTaskINSDAS';
import MyTasksTable from './MyTasksTable';
import OverallProjectTasksTable from './OverallProjectTasksTable';
import ReusableDeleteDialog from '../../common/ReusableDeleteDialog'; // Ensure this import is present

const InspectorsDashboard = () => {
  const [selectedProject, setSelectedProject] = useState(1);
  const [selectedShip, setSelectedShip] = useState(12);
  const [attendance, setAttendance] = useState({ checkIn: '', checkOut: '' });
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isDepartmentDropdownOpen, setIsDepartmentDropdownOpen] = useState(false);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [isShipDropdownOpen, setIsShipDropdownOpen] = useState(false);
  const [isEmployeeCalendarFullscreen, setIsEmployeeCalendarFullscreen] = useState(false);
  const [isWorkOrderCalendarFullscreen, setIsWorkOrderCalendarFullscreen] = useState(false);
  

  // New state for API data
  const [projects, setProjects] = useState([]);
  const [ships, setShips] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingShips, setLoadingShips] = useState(false);

  // New state for the custom pop-up (Project Wise) - RENAMED to showUpdateTaskPopup
  const [showUpdateTaskPopup, setShowUpdateTaskPopup] = useState(false);
  const [updateTaskFormData, setUpdateTaskFormData] = useState({
    project: null,
    ship: null,
    task: null,
    percentage: 0,
    status: null,
    remarks: ''
  });
  const [tasksForDropdown, setTasksForDropdown] = useState([]); // To populate task dropdown

  // MODIFICATION START: New states for edit/delete functionality
  const [selectedTaskForUpdate, setSelectedTaskForUpdate] = useState(null); // Stores task data for updating
  const [showDeleteDialog, setShowDeleteDialog] = useState(false); // Controls delete dialog visibility
  const [taskToDelete, setTaskToDelete] = useState(null); // Stores task data for deletion
  // MODIFICATION END

  // New state for Department Wise Mapping pop-up
  const [showDepartmentMapping, setShowDepartmentMapping] = useState(false);

  // New state for my tasks
  const [myTasks, setMyTasks] = useState([]); // Initialize as empty, data will be fetched via API
  const [loadingMyTasks, setLoadingMyTasks] = useState(false);

  // State for Overall Project Tasks (Fetched from API)
  const [overallProjectTasks, setOverallProjectTasks] = useState([]);
  const [loadingOverallProjectTasks, setLoadingOverallProjectTasks] = useState(false);
  const [overallProjectTasksPage, setOverallProjectTasksPage] = useState(1);
  const [overallProjectTasksTotalPages, setOverallProjectTasksTotalPages] = useState(1);

  // New state for Card Stats
  const [cardStats, setCardStats] = useState({
    total_tasks: 0,
    in_progress_tasks: 0,
    assigned_tasks: 0,
    not_assigned_tasks: 0,
    delayed_tasks: 0
  });
  const [loadingCardStats, setLoadingCardStats] = useState(false);


  // Ref for detecting clicks outside dropdowns
  const dropdownRef = useRef(null);

  // Function to get user ID from localStorage
  const getUserId = () => {
    try {
      const storedAuthDataString = localStorage.getItem('authData');
      if (storedAuthDataString) {
        const authData = JSON.parse(storedAuthDataString);
        return authData.user_id;
      }
    } catch (error) {
      console.error("Error parsing authData from localStorage:", error);
    }
    return null;
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);

    // Close dropdowns when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeAllDropdowns();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      clearInterval(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch projects on component mount
  useEffect(() => {
    const fetchProjects = async () => {
      setLoadingProjects(true);
      try {
        const response = await getRequest('/master/projects/');
        const formattedProjects = response.data.map(project => ({
          id: project.id,
          name: project.name,
          icon: Ship,
          color: getColorForProject(project.id)
        }));
        setProjects([
          { id: 'all', name: 'All', icon: Target, color: 'from-slate-600 to-slate-800' },
          ...formattedProjects
        ]);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchProjects();
  }, []);

  // Fetch ships when project is selected in the filter dropdown
  useEffect(() => {
    const fetchShips = async () => {
      if (selectedProject && selectedProject !== 'all') {
        setLoadingShips(true);
        try {
          const response = await getRequest(`/master/projects/?parent_id=${selectedProject}`);
          const formattedShips = response.data.map(ship => ({
            id: ship.id,
            name: ship.name,
            icon: Anchor,
            color: getColorForShip(ship.id)
          }));
          setShips([
            { id: 'all', name: 'All', icon: Target, color: 'from-slate-600 to-slate-800' },
            ...formattedShips
          ]);
        } catch (error) {
          console.error('Error fetching ships:', error);
        } finally {
          setLoadingShips(false);
        }
      } else {
        // Reset ships and selectedShip when "All" projects is selected
        setShips([
          { id: 'all', name: 'All', icon: Target, color: 'from-slate-600 to-slate-800' }
        ]);
        setSelectedShip('all'); // Reset selected ship when project is 'all'
        // Reset overall project tasks when no ship is selected
        setOverallProjectTasks([]);
        setOverallProjectTasksPage(1);
        setOverallProjectTasksTotalPages(1);
      }
    };

    fetchShips();
  }, [selectedProject]); // Depend on selectedProject for fetching ships

  // Fetch tasks when project or ship is selected in the update task form
  useEffect(() => {
    const fetchTasks = async () => {
      if (updateTaskFormData.project && updateTaskFormData.project !== 'all' && updateTaskFormData.ship && updateTaskFormData.ship !== 'all') {
        // Simulate API call for tasks based on selected project and ship
        const dummyTasks = [
          { id: 'task1', name: 'Inspect Engine Room', project_id: projects.find(p => p.name === 'Project Alpha')?.id, ship_id: ships.find(s => s.name === 'SS Voyager')?.id },
          { id: 'task2', name: 'Review Safety Protocols', project_id: projects.find(p => p.name === 'Project Beta')?.id, ship_id: ships.find(s => s.name === 'Sea Sprite')?.id },
          { id: 'task3', name: 'Check Communication Systems', project_id: projects.find(p => p.name === 'Project Alpha')?.id, ship_id: ships.find(s => s.name === 'SS Voyager')?.id },
          { id: 'task101', name: 'Hull Inspection - Forward Section', project_id: projects.find(p => p.name === 'Project Alpha')?.id, ship_id: ships.find(s => s.name === 'SS Voyager')?.id },
          { id: 'task102', name: 'Lifeboat Maintenance Check', project_id: projects.find(p => p.name === 'Project Alpha')?.id, ship_id: ships.find(s => s.name === 'SS Voyager')?.id },
        ];

        const filtered = dummyTasks.filter(task =>
          (task.project_id === updateTaskFormData.project) &&
          (task.ship_id === updateTaskFormData.ship)
        ).map(task => ({ label: task.name, value: task.id }));
        setTasksForDropdown(filtered);
      } else {
        setTasksForDropdown([]);
        setUpdateTaskFormData(prev => ({ ...prev, task: null }));
      }
    };
    fetchTasks();
  }, [updateTaskFormData.project, updateTaskFormData.ship, projects, ships]); // Add projects and ships to dependencies

  // Fetch My Tasks from API
  const fetchMyTasks = async () => { // Moved outside useEffect to be callable
    setLoadingMyTasks(true);
    try {
      const userId = getUserId();
      if (!userId) {
        toast.error("User ID not found for fetching my tasks.");
        setMyTasks([]);
        setLoadingMyTasks(false);
        return;
      }

      const response = await getRequest(`/pert/assigned-tasks/`);
      const formattedTasks = response.map(item => {
        let status = 'Pending';
        if (item.is_completed) {
          status = 'Completed';
        } else if (item.actual_completion_percent > 0 && item.actual_completion_percent < 100) {
          status = 'In Progress';
        }

        return {
          id: item.id,
          name: item.name,
          due_date: item.end_date,
          status: status,
          project_name: item.project,
          ship_name: item.equipment,
          task_type: item.task_type,
          contribution_calc: item.contribution_calc,
          assigned_to_name: 'N/A',
          actual_start: item.actual_start, // Ensure these are mapped for edit
          actual_finish: item.actual_finish, // Ensure these are mapped for edit
          remarks: item.remarks, // Ensure these are mapped for edit
          project_id: item.project_id, // For update form
          equipment_id: item.equipment_id, // For update form
        };
      });

      setMyTasks(formattedTasks);
    } catch (error) {
      console.error('Error fetching my tasks:', error);
      toast.error('Failed to fetch my tasks.');
      setMyTasks([]);
    } finally {
      setLoadingMyTasks(false);
    }
  };

 

// MODIFICATION START: Corrected API response parsing for Overall Project Tasks
useEffect(() => {
  const fetchOverallProjectTasks = async () => {
    if (selectedShip && selectedShip !== 'all') {
      setLoadingOverallProjectTasks(true);
      try {
        const response = await getRequest(`/transaction/task-transaction?project_id=${selectedShip}&page=${overallProjectTasksPage}`);
        // Correctly access the data from response.results.data
        setOverallProjectTasks(response.results.data || []);
        // Calculate total pages using response.count and a page size of 10
        setOverallProjectTasksTotalPages(Math.ceil(response.count / 10) || 1);

      } catch (error) {
        console.error('Error fetching overall project tasks:', error);
        setOverallProjectTasks([]);
        setOverallProjectTasksTotalPages(1);
      } finally {
        setLoadingOverallProjectTasks(false);
      }
    }
  };

  fetchOverallProjectTasks();
}, [selectedShip, overallProjectTasksPage]); // Re-fetch when ship or page changes
// MODIFICATION END

  // Fetch Card Stats based on selectedShip
  useEffect(() => {
    const fetchCardStats = async () => {
      if (selectedShip && selectedShip !== 'all') {
        setLoadingCardStats(true);
        try {
          const response = await getRequest(`/config/cards/?project_id=${selectedShip}`);
          setCardStats({
            total_tasks: response.total_tasks || 0,
            in_progress_tasks: response.in_progress_tasks || 0,
            assigned_tasks: response.assigned_tasks || 0,
            not_assigned_tasks: response.not_assigned_tasks || 0,
            // Assuming 'pending' can be derived or is not directly from API. Adjust as needed.
            pending_tasks: (response.total_tasks - response.in_progress_tasks - response.assigned_tasks - response.not_assigned_tasks) || 0, // Placeholder
            delayed_tasks: response.delayed_tasks || 0
          });
        } catch (error) {
          console.error('Error fetching card stats:', error);
          toast.error('Failed to fetch dashboard card statistics.');
          setCardStats({
            total_tasks: 0,
            in_progress_tasks: 0,
            assigned_tasks: 0,
            not_assigned_tasks: 0,
            pending_tasks: 0,
            delayed_tasks: 0
          });
        } finally {
          setLoadingCardStats(false);
        }
      } else {
        // Reset card stats if "All" ships or no ship is selected
        setCardStats({
          total_tasks: 0,
          in_progress_tasks: 0,
          assigned_tasks: 0,
          not_assigned_tasks: 0,
          pending_tasks: 0,
          delayed_tasks: 0
        });
      }
    };

    fetchCardStats();
  }, [selectedShip]); // Re-fetch when selectedShip changes

  const handlePreviousPage = () => {
    setOverallProjectTasksPage(prev => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setOverallProjectTasksPage(prev => Math.min(overallProjectTasksTotalPages, prev + 1));
  };

  // Update taskStats to use cardStats
  const taskStats = {
    total: cardStats.total_tasks,
    inProgress: cardStats.in_progress_tasks,
    assigned: cardStats.assigned_tasks,
    notAssigned: cardStats.not_assigned_tasks,
    pending: cardStats.pending_tasks, // This needs to be calculated or fetched if available
    delayed: cardStats.delayed_tasks
  };

  const departments = [
    { id: 'all', name: 'All', icon: Target, color: 'from-slate-600 to-slate-800' },
    { id: 'weapon', name: 'Weapon', icon: Shield, color: 'from-red-500 to-red-700' },
    { id: 'bfe', name: 'BFE', icon: Wrench, color: 'from-orange-500 to-orange-700' },
    { id: 'electrical', name: 'Electrical', icon: Zap, color: 'from-yellow-500 to-yellow-700' }
  ];

  const colorGradients = [
    'from-blue-500 to-blue-700',
    'from-green-500 to-green-700',
    'from-purple-500 to-purple-700',
    'from-indigo-500 to-indigo-700',
    'from-teal-500 to-teal-700',
    'from-emerald-500 to-emerald-700'
  ];

  const getColorForProject = (id) => {
    const index = projects.findIndex(p => p.id === id) % colorGradients.length;
    return colorGradients[index] || 'from-slate-500 to-slate-700';
  };

  const getColorForShip = (id) => {
    const index = ships.findIndex(s => s.id === id) % colorGradients.length;
    return colorGradients[index] || 'from-slate-500 to-slate-700';
  };

  const handleAttendance = async (type) => {
    const userId = getUserId();
    if (!userId) {
      toast.error("User ID not found. Please log in again.");
      console.error("User ID not found. Please log in again.");
      return;
    }

    const now = new Date();
    const attendanceDate = now.toISOString().split('T')[0];
    const isoTime = now.toISOString();

    let payload = {
      user: userId,
      attendance_date: attendanceDate,
      attendance_status: type === 'checkIn' ? 1 : 2,
    };

    if (type === 'checkIn') {
      payload.check_in = isoTime;
      payload.check_out = null;
    } else if (type === 'checkOut') {
      payload.check_out = isoTime;
    }

    try {
      const response = await postRequest('/config/attendance/', payload);
      console.log(`${type} response:`, response.data);
      setAttendance(prev => ({ ...prev, [type]: now.toLocaleTimeString() }));
      toast.success(`${type.replace('In', ' In').replace('Out', ' Out')} successful!`);
    } catch (error) {
      console.error(`Error during ${type}:`, error.response?.data || error.message);
      const errorMessage = error.response?.data?.detail || error.message || `Failed to ${type}.`;
      toast.error(errorMessage);
    }
  };

  const closeAllDropdowns = () => {
    setIsDepartmentDropdownOpen(false);
    setIsProjectDropdownOpen(false);
    setIsShipDropdownOpen(false);
  };

  const handleDropdownToggle = (dropdownType) => {
    switch(dropdownType) {
      case 'department':
        setIsDepartmentDropdownOpen(prev => !prev);
        setIsProjectDropdownOpen(false);
        setIsShipDropdownOpen(false);
        break;
      case 'project':
        setIsProjectDropdownOpen(prev => !prev);
        setIsDepartmentDropdownOpen(false);
        setIsShipDropdownOpen(false);
        break;
      case 'ship':
        setIsShipDropdownOpen(prev => !prev);
        setIsShipDropdownOpen(false); // Close department dropdown
        setIsProjectDropdownOpen(false); // Close project dropdown
        break;
      default:
        break;
    }
  };

  const updateTaskFormFields = [
    {
      name: 'project',
      label: 'Project',
      type: 'select',
      options: projects.filter(p => p.id !== 'all').map(p => ({ label: p.name, value: p.id })),
      required: true,
      placeholder: 'Select Project',
    },
    {
      name: 'ship',
      label: 'Ship',
      type: 'select',
      options: ships.filter(s => s.id !== 'all').map(s => ({ label: s.name, value: s.id })),
      required: true,
      placeholder: 'Select Ship',
      disabled: !updateTaskFormData.project || updateTaskFormData.project === 'all',
    },
    {
      name: 'task',
      label: 'Task',
      type: 'select',
      options: tasksForDropdown,
      required: true,
      placeholder: 'Select Task',
      disabled: !updateTaskFormData.ship || updateTaskFormData.ship === 'all',
    },
    {
      name: 'percentage',
      label: 'Completion Percentage',
      type: 'slider', // Changed to slider type
      min: 0,
      max: 100,
      step: 1,
      showValue: true, // Display the current value
      required: true,
      disabled: updateTaskFormData.status === 'Completed', // Disable if status is Completed
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { label: 'In Progress', value: 'In Progress' },
        { label: 'Completed', value: 'Completed' },
        { label: 'Pending', value: 'Pending' },
        { label: 'Delayed', value: 'Delayed' },
      ],
      required: true,
      placeholder: 'Select Status',
    },
    {
      name: 'remarks',
      label: 'Remarks',
      type: 'textarea',
      placeholder: 'Add any remarks or notes',
    },
  ];

  const onUpdateTaskFormChange = (e, field) => {
    const { name, value } = e.target;
    // Special handling for status change
    if (name === 'status' && value === 'Completed') {
      setUpdateTaskFormData(prev => ({
        ...prev,
        status: value,
        percentage: 100 // Set percentage to 100 when status is "Completed"
      }));
    } else {
      setUpdateTaskFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleUpdateTaskSubmit = (e) => {
    e.preventDefault();
    console.log("Update Task Form Data:", updateTaskFormData);
    // Here you would make your API call to update the task
    // Example: postRequest('/tasks/update/', updateTaskFormData);
    toast.success("Task update initiated! (Simulated)");
    setShowUpdateTaskPopup(false);
    setUpdateTaskFormData({
      project: null,
      ship: null,
      task: null,
      percentage: 0,
      status: null,
      remarks: ''
    }); // Reset form
    setSelectedTaskForUpdate(null); // Clear selected task
  };

  // MODIFICATION START: Handler functions for MyTasksTable actions and modals
  const handleEditTask = async (task) => {
    setLoadingMyTasks(true); // Indicate loading while fetching task details
    try {
      // Fetch full task details using the task's ID
      const response = await getRequest(`/pert/assigned-tasks/?id=${task.id}`);
      if (response.length > 0) {
        const fullTaskDetails = response[0]; // Assuming the API returns an array
        setSelectedTaskForUpdate(fullTaskDetails);
        setShowUpdateTaskPopup(true);
      } else {
        toast.error("Task details not found.");
      }
    } catch (error) {
      console.error("Error fetching task details for edit:", error);
      toast.error("Failed to fetch task details for editing.");
    } finally {
      setLoadingMyTasks(false);
    }
  };

  const handleCloseUpdateTaskPopup = () => {
    setShowUpdateTaskPopup(false);
    setSelectedTaskForUpdate(null); // Clear selected task
    // Reset form data to default or based on new requirements
    setUpdateTaskFormData({
      project: null,
      ship: null,
      task: null,
      percentage: 0,
      status: null,
      remarks: ''
    });
    fetchMyTasks(); // Re-fetch my tasks to reflect potential updates
  };

  const handleDeleteTask = (task) => {
    setTaskToDelete(task);
    setShowDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
    setTaskToDelete(null); // Clear task to delete
  };

  const confirmDeleteTask = async () => {
    if (taskToDelete) {
      setLoadingMyTasks(true); // Indicate loading for delete operation
      try {
        const payload = {
          id: taskToDelete.id,
          status_db: 3 // As per requirement, set status_db to 3 for deletion
        };
        await postRequest(`/pert/task-transaction-api/`, payload);
        toast.success(`Task "${taskToDelete.name}" deleted successfully!`);
        // Filter out the deleted task from `myTasks` state
        setMyTasks(prevTasks => prevTasks.filter(task => task.id !== taskToDelete.id));
      } catch (error) {
        console.error("Error deleting task:", error.response?.data || error.message);
        toast.error(`Failed to delete task: ${error.response?.data?.detail || error.message}`);
      } finally {
        handleCloseDeleteDialog(); // Close dialog after action
        setLoadingMyTasks(false);
      }
    }
  };
  // MODIFICATION END

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-100 to-purple-100 relative overflow-hidden text-slate-800">
      {/* ToastContainer for notifications */}
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-300/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-300/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-indigo-300/20 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      {/* Header */}
      <div className="relative z-10">
        <div className="flex items-center justify-between p-6 bg-white/80 backdrop-blur-md border-b border-blue-200 shadow-sm">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-xl shadow-lg">
              <Anchor size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Inspectors Dashboard</h1>
              <p className="text-blue-600 text-sm">Inspectors Dashboard</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-slate-700 font-medium">{currentTime.toLocaleTimeString()}</p>
              <p className="text-blue-600 text-sm">{currentTime.toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Filter Dropdowns - Fixed z-index issue */}
          <div ref={dropdownRef} className="bg-white/80 backdrop-blur-md border border-blue-200 rounded-2xl p-6 shadow-md relative z-10">
            <h2 className="text-xl font-semibold text-slate-800 mb-6 flex items-center">
              <Filter className="mr-3 text-blue-500" size={24} />
              Filter Options
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Project Dropdown */}
              <div className="relative">
                <label className="block text-sm font-medium text-slate-700 mb-2">Project</label>
                <button
                  onClick={() => handleDropdownToggle('project')}
                  className="w-full flex justify-between items-center px-4 py-3 bg-blue-50 text-slate-700 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors shadow-sm"
                >
                  <span className="font-medium">
                    {loadingProjects
                      ? 'Loading...'
                      : (projects.find(p => p.id === selectedProject)?.name || 'Select Project')}
                  </span>
                  {isProjectDropdownOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {isProjectDropdownOpen && (
                  <div className="absolute top-full left-0 w-full bg-white border border-blue-200 rounded-lg shadow-xl mt-2 z-[200] max-h-60 overflow-y-auto">
                    {projects.map((project) => (
                      <button
                        key={project.id}
                        onClick={() => {
                          setSelectedProject(project.id);
                          closeAllDropdowns();
                        }}
                        className={`w-full text-left px-4 py-3 flex items-center space-x-2 hover:bg-blue-50 transition-colors ${
                          selectedProject === project.id ? 'bg-blue-100 font-semibold' : ''
                        }`}
                      >
                        <project.icon size={18} className={selectedProject === project.id ? 'text-blue-600' : 'text-slate-600'} />
                        <span>{project.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Ship Dropdown */}
              <div className="relative">
                <label className="block text-sm font-medium text-slate-700 mb-2">Ship</label>
                <button
                  onClick={() => handleDropdownToggle('ship')}
                  className={`w-full flex justify-between items-center px-4 py-3 bg-blue-50 text-slate-700 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors shadow-sm ${
                    selectedProject === 'all' ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  disabled={selectedProject === 'all' || loadingProjects || loadingShips}
                >
                  <span className="font-medium">
                    {loadingShips
                      ? 'Loading...'
                      : (ships.find(s => s.id === selectedShip)?.name || 'Select Ship')}
                  </span>
                  {isShipDropdownOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {isShipDropdownOpen && selectedProject !== 'all' && (
                  <div className="absolute top-full left-0 w-full bg-white border border-blue-200 rounded-lg shadow-xl mt-2 z-[200] max-h-60 overflow-y-auto">
                    {ships.map((ship) => (
                      <button
                        key={ship.id}
                        onClick={() => {
                          setSelectedShip(ship.id);
                          closeAllDropdowns();
                        }}
                        className={`w-full text-left px-4 py-3 flex items-center space-x-2 hover:bg-blue-50 transition-colors ${
                          selectedShip === ship.id ? 'bg-blue-100 font-semibold' : ''
                        }`}
                      >
                        <ship.icon size={18} className={selectedShip === ship.id ? 'text-blue-600' : 'text-slate-600'} />
                        <span>{ship.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Stats Grid - Reduced z-index to prevent interference */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 relative z-0">
            {[
              { title: 'Total Tasks', value: taskStats.total, icon: Target, gradient: 'from-blue-400 to-indigo-500', change: '' },
              { title: 'In Progress', value: taskStats.inProgress, icon: Clock, gradient: 'from-orange-400 to-red-500', change: '' },
              { title: 'Assigned', value: taskStats.assigned, icon: Users, gradient: 'from-emerald-400 to-teal-500', change: '' },
              { title: 'Not Assigned', value: taskStats.notAssigned, icon: AlertCircle, gradient: 'from-yellow-400 to-orange-500', change: '' },
              { title: 'Pending', value: taskStats.pending, icon: CheckCircle, gradient: 'from-purple-400 to-pink-500', change: '' },
              { title: 'Delayed', value: taskStats.delayed, icon: XCircle, gradient: 'from-red-400 to-rose-500', change: '' }
            ].map((stat, index) => (
              <div key={index} className="group relative">
                <div className="relative bg-white/80 backdrop-blur-md border border-blue-200 rounded-2xl p-6 hover:bg-white/90 transition-all duration-300 hover:scale-105 shadow-md hover:shadow-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 bg-gradient-to-r ${stat.gradient} rounded-xl shadow-lg`}>
                      <stat.icon className="text-white" size={20} />
                    </div>
                    {loadingCardStats ? (
                      <span className="text-sm text-slate-500">Loading...</span>
                    ) : (
                      stat.change && (
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          stat.change.startsWith('+') ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {stat.change}
                        </span>
                      )
                    )}
                  </div>
                  <h3 className="text-slate-600 text-sm font-medium mb-1">{stat.title}</h3>
                  <p className="text-3xl font-bold text-slate-800">
                    {loadingCardStats ? '...' : stat.value}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Attendance & Task Management */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative z-10">
            {/* Attendance */}
            <div className="bg-white/80 backdrop-blur-md border border-blue-200 rounded-2xl p-6 shadow-md flex flex-col justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-800 mb-6 flex items-center">
                  <Clock className="mr-3 text-blue-500" size={24} />
                  Attendance Management
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => handleAttendance('checkIn')}
                    className="group relative bg-gradient-to-r from-emerald-500 to-teal-600 text-white p-4 rounded-xl font-semibold text-lg hover:from-emerald-600 hover:to-teal-700 transition-all duration-300 transform hover:-translate-y-1 shadow-lg flex items-center justify-center space-x-2"
                  >
                    <CheckCircle size={20} className="group-hover:scale-110 transition-transform" />
                    <span>Check In</span>
                  </button>
                  <button
                    onClick={() => handleAttendance('checkOut')}
                    className="group relative bg-gradient-to-r from-red-500 to-rose-600 text-white p-4 rounded-xl font-semibold text-lg hover:from-red-600 hover:to-rose-700 transition-all duration-300 transform hover:-translate-y-1 shadow-lg flex items-center justify-center space-x-2"
                  >
                    <XCircle size={20} className="group-hover:scale-110 transition-transform" />
                    <span>Check Out</span>
                  </button>
                </div>
              </div>
              {(attendance.checkIn || attendance.checkOut) && (
                <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100 text-slate-700 animate-fadeInUp">
                  {attendance.checkIn && (
                    <p className="text-emerald-600 text-sm mb-2 flex items-center">
                      <CheckCircle size={16} className="mr-2" />
                      <span className="font-medium">Checked in at:</span> {attendance.checkIn}
                    </p>
                  )}
                  {attendance.checkOut && (
                    <p className="text-red-600 text-sm flex items-center">
                      <XCircle size={16} className="mr-2" />
                      <span className="font-medium">Checked out at:</span> {attendance.checkOut}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Task Management with "Update Task" Button */}
            <div className="bg-white/80 backdrop-blur-md border border-blue-200 rounded-2xl p-6 shadow-md flex flex-col justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-800 mb-6 flex items-center">
                  <ClipboardList className="mr-3 text-blue-500" size={24} />
                  Task Management
                </h2>
                <div className="grid grid-cols-1 gap-4">
                  <button
                    onClick={() => {setSelectedTaskForUpdate(null); setShowUpdateTaskPopup(true)}} // This button will now open a blank form
                    className="group relative bg-gradient-to-r from-blue-400 to-indigo-500 text-white p-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:-translate-y-1 shadow-lg flex items-center justify-center space-x-2"
                  >
                    <CheckSquare size={20} className="group-hover:scale-110 transition-transform" />
                    <span>Update Task</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Main Dashboard Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative z-10">
            {/* Employee Shift Calendar */}
            <div className={`bg-white/80 backdrop-blur-md border border-blue-200 rounded-2xl p-6 shadow-md flex flex-col ${isEmployeeCalendarFullscreen ? 'fixed inset-0 z-[9999] bg-white/95 m-4' : ''}`} style={{ height: isEmployeeCalendarFullscreen ? 'auto' : '700px' }}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-slate-800 flex items-center">
                  <Calendar className="mr-3 text-blue-500" size={24} />
                  Employee Shift Calendar
                </h2>
                <button
                  onClick={() => setIsEmployeeCalendarFullscreen(!isEmployeeCalendarFullscreen)}
                  className="p-2 bg-blue-100 rounded-full hover:bg-blue-200 transition-colors text-blue-600"
                  title={isEmployeeCalendarFullscreen ? "Exit Fullscreen" : "View Fullscreen"}
                >
                  {isEmployeeCalendarFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <EmployeeShiftCalender />
              </div>
            </div>

            {/* Work Order Calendar */}
            <div className={`bg-white/80 backdrop-blur-md border border-blue-200 rounded-2xl p-6 shadow-md flex flex-col ${isWorkOrderCalendarFullscreen ? 'fixed inset-0 z-[9999] bg-white/95 m-4' : ''}`} style={{ height: isWorkOrderCalendarFullscreen ? 'auto' : '700px' }}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-slate-800 flex items-center">
                  <BarChart3 className="mr-3 text-blue-500" size={24} />
                  Work Order Calendar
                </h2>
                <button
                  onClick={() => setIsWorkOrderCalendarFullscreen(!isWorkOrderCalendarFullscreen)}
                  className="p-2 bg-blue-100 rounded-full hover:bg-blue-200 transition-colors text-blue-600"
                  title={isWorkOrderCalendarFullscreen ? "Exit Fullscreen" : "View Fullscreen"}
                >
                  {isWorkOrderCalendarFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <WorkOrderCalender />
              </div>
            </div>

            {/* My Tasks Section - Now using MyTasksTable component */}
            {/* MODIFICATION START: Pass handleEditTask and handleDeleteTask as props */}
            <MyTasksTable 
              myTasks={myTasks} 
              loadingMyTasks={loadingMyTasks}
              onEditTask={handleEditTask}
              onDeleteTask={handleDeleteTask}
            />
            {/* MODIFICATION END */}

            {/* Overall Project Tasks Section - Now using OverallProjectTasksTable component */}
            <OverallProjectTasksTable
              overallProjectTasks={overallProjectTasks}
              loadingOverallProjectTasks={loadingOverallProjectTasks}
              overallProjectTasksPage={overallProjectTasksPage}
              overallProjectTasksTotalPages={overallProjectTasksTotalPages}
              handlePreviousPage={handlePreviousPage}
              handleNextPage={handleNextPage}
            />
          </div>
        </div>
      </div>

      {/* Update Task Pop-up Modal (Using UpdateTaskINSDAS) */}
      {/* MODIFICATION START: Pass selectedTask and onClose to UpdateTaskINSDAS */}
      {showUpdateTaskPopup && (
        <UpdateTaskINSDAS
          onClose={handleCloseUpdateTaskPopup}
          visible={showUpdateTaskPopup}
          selectedTask={selectedTaskForUpdate} // Pass the selected task for pre-population
        />
      )}
      {/* MODIFICATION END */}

      {/* Department Wise Mapping Pop-up (Existing) */}
      {showDepartmentMapping && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-2xl w-full mx-4 animate-fade-in max-w-6xl h-5/6 overflow-y-auto">
            <DepartmentWiseMapping onBack={() => setShowDepartmentMapping(false)} />
          </div>
        </div>
      )}

      {/* MODIFICATION START: ReusableDeleteDialog */}
      {showDeleteDialog && taskToDelete && (
        <ReusableDeleteDialog
          visible={showDeleteDialog}
          onHide={handleCloseDeleteDialog}
          onDelete={confirmDeleteTask}
          title="Confirm Task Deletion"
          description={`Are you sure you want to delete the task "${taskToDelete.name}"? This action cannot be undone.`}
          deleteLabel="Delete Task"
          loading={loadingMyTasks} // Adjust based on your actual delete API call's loading state
        />
      )}
      {/* MODIFICATION END */}
    </div>
  );
};

export default InspectorsDashboard;